import React, { useState, useEffect } from 'react';
import api from '../api';
import { Save, Building, CreditCard, AlertCircle, CheckCircle } from 'lucide-react';

const BankDetails = () => {
    const [formData, setFormData] = useState({
        account_holder_name: '',
        account_number: '',
        bank_name: '',
        ifsc_code: '',
        branch_name: '',
        account_type: 'Savings'
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchBankDetails();
    }, []);

    const fetchBankDetails = async () => {
        try {
            const res = await api.get('/settings/bank-details');
            if (res.data) {
                setFormData(res.data);
            }
        } catch (err) {
            if (err.response && err.response.status !== 404) {
                console.error("Failed to fetch bank details", err);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const validateForm = () => {
        const nameRegex = /^[a-zA-Z0-9\s]+$/;
        
        if (!formData.account_holder_name.trim()) {
            return "Account holder name is required";
        }
        if (!nameRegex.test(formData.account_holder_name)) {
            return "Account holder name cannot contain special characters";
        }
        
        if (!formData.bank_name.trim()) {
            return "Bank name is required";
        }
        if (!nameRegex.test(formData.bank_name)) {
            return "Bank name cannot contain special characters";
        }
        const accountRegex = /^\d{9,18}$/;
        if (!accountRegex.test(formData.account_number)) {
            return "Invalid account number. Must be 9-18 digits.";
        }

        const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
        if (!ifscRegex.test(formData.ifsc_code.toUpperCase())) {
            return "Invalid IFSC Code. Format: 4 letters, 1 zero, 6 characters.";
        }
        
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        const error = validateForm();
        if (error) {
            setMessage({ type: 'error', text: error });
            return;
        }

        setSaving(true);

        try {
            await api.post('/settings/bank-details', formData);
            setMessage({ type: 'success', text: 'Bank details saved successfully!' });
        } catch (err) {
            console.error("Failed to save bank details", err);
            setMessage({ type: 'error', text: 'Failed to save details. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '10px 12px',
        borderRadius: '8px',
        border: '1px solid var(--border)',
        marginTop: '6px',
        fontSize: '14px'
    };

    const labelStyle = {
        display: 'block',
        fontSize: '13px',
        fontWeight: '600',
        color: 'var(--text-muted)',
        marginBottom: '6px',
        textTransform: 'uppercase',
        letterSpacing: '0.02em'
    };

    if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}>Loading bank details...</div>;

    return (
        <div className="bank-details-container" style={{ maxWidth: '600px', padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px', gap: '10px' }}>
                <div style={{ background: '#eef2ff', padding: '10px', borderRadius: '12px', color: 'rgb(39, 1, 88)' }}>
                    <Building size={24} />
                </div>
                <div>
                    <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--proto-white)', margin: 0 }}>Bank Account Details</h2>
                    <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px' }}>
                        For receiving payments and refunds
                    </p>
                </div>
            </div>

            {message.text && (
                <div style={{
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: message.type === 'error' ? '#fef2f2' : '#f0fdf4',
                    color: message.type === 'error' ? '#ef4444' : '#10b981',
                    border: `1px solid ${message.type === 'error' ? '#fecaca' : '#bbf7d0'}`
                }}>
                    {message.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
                    {message.text}
                </div>
            )}

            <form onSubmit={handleSubmit} style={{ background: 'var(--graphite-dark)', padding: '24px', borderRadius: '16px', border: '1px solid var(--border)', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>

                <div>
                    <label style={labelStyle}>Account Holder Name</label>
                    <input
                        type="text"
                        name="account_holder_name"
                        value={formData.account_holder_name}
                        onChange={handleChange}
                        placeholder="As per bank records"
                        required
                        style={inputStyle}
                    />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                        <label style={labelStyle}>Bank Name</label>
                        <input
                            type="text"
                            name="bank_name"
                            value={formData.bank_name}
                            onChange={handleChange}
                            placeholder="e.g. HDFC Bank"
                            required
                            style={inputStyle}
                        />
                    </div>
                    <div>
                        <label style={labelStyle}>Account Type</label>
                        <select
                            name="account_type"
                            value={formData.account_type}
                            onChange={handleChange}
                            style={inputStyle}
                        >
                            <option value="Savings">Savings</option>
                            <option value="Current">Current</option>
                        </select>
                    </div>
                </div>

                <div>
                    <label style={labelStyle}>Account Number</label>
                    <input
                        type="password"
                        name="account_number"
                        value={formData.account_number}
                        onChange={handleChange}
                        placeholder="Enter account number"
                        required
                        style={inputStyle}
                    />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                        <label style={labelStyle}>IFSC Code</label>
                        <input
                            type="text"
                            name="ifsc_code"
                            value={formData.ifsc_code}
                            onChange={handleChange}
                            placeholder="e.g. HDFC0001234"
                            required
                            style={{ ...inputStyle, textTransform: 'uppercase' }}
                        />
                    </div>
                    <div>
                        <label style={labelStyle}>Branch Name</label>
                        <input
                            type="text"
                            name="branch_name"
                            value={formData.branch_name}
                            onChange={handleChange}
                            placeholder="e.g. Koramangala"
                            style={inputStyle}
                        />
                    </div>
                </div>

                <div style={{ marginTop: '32px' }}>
                    <button
                        type="submit"
                        disabled={saving}
                        style={{
                            width: '100%',
                            padding: '12px',
                            backgroundColor: 'rgb(39, 1, 88)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '600',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            opacity: saving ? 0.7 : 1
                        }}
                    >
                        {saving ? 'Saving...' : <><Save size={18} /> Save Bank Details</>}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default BankDetails;


